/**
 * CeMM's typefaces, shipped with the app and registered here.
 *
 * From the CeMM colour and font chart: Space Grotesk Medium for the logo,
 * headlines and titles; Source Sans Pro Regular for running text. Source
 * Sans Pro is now published as Source Sans 3 -- the same design, renamed by
 * Adobe -- which is what the variable font is. Source Sans has a true italic,
 * loaded too; Space Grotesk has none, and titles are not set in italics.
 *
 * Latin and Latin Extended only -- enough for every name in the institute
 * (Dvořák, Łukasz, Erdős). Anything outside these ranges falls back to the
 * system font.
 *
 * Registered from JavaScript rather than declared in a stylesheet, because of
 * where they have to come from. Bun's bundler inlines a stylesheet's fonts as
 * data: URLs, whatever loader is configured, and production's content security
 * policy -- set on IT's proxy -- allows fonts only from 'self': the embedded
 * fonts were refused there and every page fell back to a system font (v0.7.0).
 * Imported from JavaScript, each file is emitted as a file of its own and
 * served from the app's own origin, in development as in production. As
 * files, they are also cached on their own, and the browser fetches only the
 * ranges a page actually uses.
 */
import sourceSans3LatinExtWghtNormal from "../node_modules/@fontsource-variable/source-sans-3/files/source-sans-3-latin-ext-wght-normal.woff2";
import sourceSans3LatinWghtNormal from "../node_modules/@fontsource-variable/source-sans-3/files/source-sans-3-latin-wght-normal.woff2";
import sourceSans3LatinExtWghtItalic from "../node_modules/@fontsource-variable/source-sans-3/files/source-sans-3-latin-ext-wght-italic.woff2";
import sourceSans3LatinWghtItalic from "../node_modules/@fontsource-variable/source-sans-3/files/source-sans-3-latin-wght-italic.woff2";
import spaceGroteskLatinExtWghtNormal from "../node_modules/@fontsource-variable/space-grotesk/files/space-grotesk-latin-ext-wght-normal.woff2";
import spaceGroteskLatinWghtNormal from "../node_modules/@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2";

type Face = { family: string; url: string; style: "normal" | "italic"; weight: string; unicodeRange: string };

const LATIN_EXT_SOURCE_SANS = "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF";
const LATIN_EXT_SPACE_GROTESK = "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF";
const LATIN = "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";

const FACES: Face[] = [
  { family: "Source Sans 3 Variable", url: sourceSans3LatinExtWghtNormal, style: "normal", weight: "200 900", unicodeRange: LATIN_EXT_SOURCE_SANS },
  { family: "Source Sans 3 Variable", url: sourceSans3LatinWghtNormal, style: "normal", weight: "200 900", unicodeRange: LATIN },
  { family: "Source Sans 3 Variable", url: sourceSans3LatinExtWghtItalic, style: "italic", weight: "200 900", unicodeRange: LATIN_EXT_SOURCE_SANS },
  { family: "Source Sans 3 Variable", url: sourceSans3LatinWghtItalic, style: "italic", weight: "200 900", unicodeRange: LATIN },
  { family: "Space Grotesk Variable", url: spaceGroteskLatinExtWghtNormal, style: "normal", weight: "300 700", unicodeRange: LATIN_EXT_SPACE_GROTESK },
  { family: "Space Grotesk Variable", url: spaceGroteskLatinWghtNormal, style: "normal", weight: "300 700", unicodeRange: LATIN },
];

// Added once, at start-up. A face added this way is loaded the first time text
// in its range and style is drawn, as an @font-face one would be.
for (const face of FACES) {
  document.fonts.add(new FontFace(face.family, `url(${face.url}) format("woff2")`, {
    style: face.style,
    weight: face.weight,
    unicodeRange: face.unicodeRange,
    display: "swap",
  }));
}
